import { useTranslation } from 'react-i18next';
import { Coins } from 'lucide-react';

import { env } from '@/env';
import { useGetWalletsQuery } from '@/services/qbankApi';
import { QBANK_DEFAULT_COIN } from '@/types/qbank';

/**
 * QBank gamification HUD — a compact pill anchored top-right (above the Hub
 * banner) showing the player's currency balance + EXP level + XP-to-next, all
 * in one row. Account-wide (not game-specific). Behind `featureQbankEnabled`
 * (OFF in PROD).
 *
 * Visibility:
 * - Hidden on disabled flag / loading / error (a 401 means the caller lacks the
 *   `qbank:wallet:read` scope → not entitled → show nothing, never a broken bar).
 * - When entitled (200) but with NO currency wallet yet (QBank creates rows
 *   lazily on first credit → `[]`), we still render "0 QC" so a zero balance is
 *   visible rather than hidden.
 *
 * Currency coins have `level == null` (`amount` = balance). EXP coins carry a
 * non-null `level` (+ `toNextLevel` = XP remaining to next level). The per-level
 * bracket for an exact % fill isn't exposed by `/wallets/get`, so we show
 * "{n} XP →" (truthful) rather than a fabricated progress percentage.
 */
export function QbankHud() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetWalletsQuery(undefined, {
    skip: !env.featureQbankEnabled,
  });

  // Not entitled / loading / disabled → render nothing (no broken bar).
  if (!env.featureQbankEnabled || isLoading || isError) return null;

  const wallets = data ?? [];
  const currency =
    wallets.find((w) => w.level == null && w.isDefault) ??
    wallets.find((w) => w.level == null);
  const exp = wallets.find((w) => w.level != null);

  // Entitled: always show a currency balance — the real one, or 0 of the default
  // coin when the user has no wallet yet.
  const coinCode = currency?.code || QBANK_DEFAULT_COIN.code;
  const coinAmount = Number(currency?.amount ?? 0);

  return (
    <div className="flex justify-end">
      <section
        aria-label="QBank"
        className="inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/5 py-1 pl-2 pr-2.5"
      >
        <span className="flex items-center gap-1.5">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/15 text-brand ring-1 ring-brand/40">
            <Coins aria-hidden className="h-3.5 w-3.5" />
          </span>
          <span className="flex items-baseline gap-1">
            <span className="text-sm font-extrabold tabular-nums text-fg">
              {coinAmount.toLocaleString()}
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wide text-fg-muted">
              {coinCode}
            </span>
          </span>
        </span>

        {exp && (
          <span className="flex items-center gap-1.5 border-l border-brand/20 pl-2">
            <span className="rounded bg-brand px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-fg">
              {t('qbank.level', { defaultValue: 'Lv {{n}}', n: exp.level ?? 0 })}
            </span>
            {exp.toNextLevel != null && exp.toNextLevel > 0 && (
              <span className="text-[10px] font-semibold tabular-nums text-fg-muted">
                {t('qbank.toNext', {
                  defaultValue: '{{n}} XP →',
                  n: Number(exp.toNextLevel).toLocaleString(),
                })}
              </span>
            )}
          </span>
        )}
      </section>
    </div>
  );
}
